import React from 'react';
import { X, Receipt, Layers, CreditCard, ArrowRight, ShieldCheck, AlertTriangle } from 'lucide-react';
import { ReconciledItem } from '../../types/reconciliation';
import { StatusBadge } from '../common/Badge';

interface BillsBreakdownModalProps {
  item: ReconciledItem | null;
  onClose: () => void;
  onOpenAi?: (item: ReconciledItem) => void;
}

export const BillsBreakdownModal: React.FC<BillsBreakdownModalProps> = ({
  item,
  onClose,
  onOpenAi,
}) => {
  if (!item) return null;

  const isExact = item.status === 'Exact Match';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-2xl overflow-hidden animate-scale-in my-8">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-850">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Payment ID: {item.paymentId}
                </h3>
                <StatusBadge status={item.status} size="sm" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Customer: {item.customerName} &bull; Payment Date: {item.paymentDate}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Amount Comparison Banner */}
        <div className="p-5 bg-slate-100/50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 grid grid-cols-3 gap-3 text-center">
          <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
              Razorpay Received
            </span>
            <div className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
              ₹{item.razorpayAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>

          <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
              ERP Consolidated Total
            </span>
            <div className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
              ₹{item.erpTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>

          <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
              Variance Difference
            </span>
            <div
              className={`text-base font-bold mt-0.5 ${
                Math.abs(item.differenceAmount) < 0.01
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : item.differenceAmount > 0
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-purple-600 dark:text-purple-400'
              }`}
            >
              {item.differenceAmount > 0 ? '+' : ''}
              ₹{item.differenceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Individual Bills Breakdown List */}
        <div className="p-5 max-h-80 overflow-y-auto space-y-3">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-700 dark:text-slate-300">
            <span>Aggregated ERP Bills ({item.billCount})</span>
            <span className="text-slate-500 font-normal">
              1-to-many relationship ledger breakdown
            </span>
          </div>

          {item.erpBills.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 dark:bg-slate-850 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
              No ERP bills are linked to this Payment ID. (Missing ERP Bill Reference)
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              {item.erpBills.map((bill, idx) => (
                <div
                  key={idx}
                  className="p-3.5 bg-white dark:bg-slate-900 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-850 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-mono text-xs font-semibold text-slate-600 dark:text-slate-300">
                      #{idx + 1}
                    </div>
                    <div>
                      <div className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">
                        {bill.billNumber}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        Posting Date: {bill.postingDate || 'N/A'} {bill.customerName ? `&bull; ${bill.customerName}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold font-mono text-slate-900 dark:text-white">
                      ₹{bill.billAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                      Sub-bill component
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Refund note if applicable */}
          {item.refundStatus === 'Refunded' && (
            <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 text-xs text-sky-800 dark:text-sky-300 flex items-center justify-between">
              <div>
                <span className="font-semibold">Refund Captured: </span>
                ₹{item.refundAmount.toLocaleString('en-IN')} on {item.refundDate}
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded bg-sky-100 dark:bg-sky-900 font-bold">
                Reversal Required
              </span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 flex items-center justify-between">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {item.aiNotes}
          </div>

          <div className="flex items-center gap-2">
            {onOpenAi && (
              <button
                onClick={() => {
                  onClose();
                  onOpenAi(item);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 dark:bg-brand-950 dark:hover:bg-brand-900 dark:text-brand-300 text-xs font-semibold border border-brand-200 dark:border-brand-800 transition"
              >
                <span>AI Diagnostics</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-semibold transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
